/**
 * Lead service - API interactions for lead management
 */

import { apiClient } from "@/services/apiClient";
import type {
  Lead,
  LeadStatus,
  LeadNote,
  LeadFilterParams,
  CreateLeadData,
  UpdateLeadData,
  LeadTag,
  LogContactData,
  CSVPreviewResult,
  CSVImportResult,
  ConvertLeadResult,
  BulkUpdateData,
  BulkUpdateResponse,
  BulkDeleteResponse,
  PlaceResult,
  PlacesSearchResult,
  PlacesImportResult,
  PlacesCategoriesResult,
  EmailSend,
  SendEmailData,
} from "./Lead.types";

interface LeadsResponse {
  success: boolean;
  leads: Lead[];
  error?: string;
}

interface LeadResponse {
  success: boolean;
  lead: Lead;
  error?: string;
}

interface LeadNotesResponse {
  success: boolean;
  notes: LeadNote[];
  error?: string;
}

interface LeadNoteResponse {
  success: boolean;
  note: LeadNote;
  error?: string;
}

export const leadService = {
  /**
   * Get all leads with optional filtering
   */
  async getLeads(includeConverted: boolean = false): Promise<Lead[]> {
    const url = includeConverted
      ? "/api/leads?include_converted=true"
      : "/api/leads";
    const response: LeadsResponse = await apiClient.get(url);

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch leads");
    }

    return response.leads;
  },

  /**
   * Get leads with filtering and sorting
   */
  async getLeadsFiltered(params: LeadFilterParams = {}): Promise<Lead[]> {
    const queryParams = new URLSearchParams();

    if (params.sortBy) queryParams.set("sort_by", params.sortBy);
    if (params.sortOrder) queryParams.set("sort_order", params.sortOrder);
    if (params.status) queryParams.set("status", params.status);
    if (params.sector) queryParams.set("sector", params.sector);
    if (params.city) queryParams.set("city", params.city);
    if (params.cityEmpty) queryParams.set("city_empty", "true");
    if (params.includeConverted) queryParams.set("include_converted", "true");

    const url = `/api/leads?${queryParams.toString()}`;
    const response: LeadsResponse = await apiClient.get(url);

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch leads");
    }

    return response.leads;
  },

  /**
   * Get a single lead by ID
   */
  async getLead(leadId: string): Promise<Lead> {
    const response: LeadResponse = await apiClient.get(`/api/leads/${leadId}`);

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch lead");
    }

    return response.lead;
  },

  /**
   * Get leads by status
   */
  async getLeadsByStatus(status: LeadStatus): Promise<Lead[]> {
    const response: LeadsResponse = await apiClient.get(
      `/api/leads?status=${status}`
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch leads");
    }

    return response.leads;
  },

  /**
   * Get leads by sector
   */
  async getLeadsBySector(sector: string): Promise<Lead[]> {
    const response: LeadsResponse = await apiClient.get(
      `/api/leads?sector=${encodeURIComponent(sector)}`
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch leads");
    }

    return response.leads;
  },

  /**
   * Create a new lead
   */
  async createLead(data: CreateLeadData): Promise<Lead> {
    const response: LeadResponse = await apiClient.post("/api/leads", data);

    if (!response.success) {
      throw new Error(response.error || "Failed to create lead");
    }

    return response.lead;
  },

  /**
   * Update an existing lead
   */
  async updateLead(leadId: string, data: UpdateLeadData): Promise<Lead> {
    const response: LeadResponse = await apiClient.put(
      `/api/leads/${leadId}`,
      data
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to update lead");
    }

    return response.lead;
  },

  /**
   * Delete a lead
   */
  async deleteLead(leadId: string): Promise<void> {
    const response = await apiClient.delete(`/api/leads/${leadId}`);

    if (!response.success) {
      throw new Error(response.error || "Failed to delete lead");
    }
  },

  /**
   * Log a contact attempt
   */
  async logContact(leadId: string, data: LogContactData): Promise<Lead> {
    const response: LeadResponse = await apiClient.post(
      `/api/leads/${leadId}/contact`,
      data
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to log contact");
    }

    return response.lead;
  },

  /**
   * Update lead status
   */
  async updateStatus(leadId: string, status: LeadStatus): Promise<Lead> {
    const response: LeadResponse = await apiClient.put(
      `/api/leads/${leadId}/status`,
      { status }
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to update status");
    }

    return response.lead;
  },

  /**
   * Convert a qualified lead to a client
   */
  async convertToClient(leadId: string): Promise<ConvertLeadResult> {
    const response: ConvertLeadResult = await apiClient.post(
      `/api/leads/${leadId}/convert`
    );

    return response;
  },

  /**
   * Preview CSV import
   */
  async previewCSV(
    file: File,
    fieldMapping?: Record<string, string>
  ): Promise<CSVPreviewResult> {
    const formData = new FormData();
    formData.append("file", file);

    if (fieldMapping) {
      formData.append("field_mapping", JSON.stringify(fieldMapping));
    }

    const response = await apiClient.postFormData(
      "/api/leads/import/preview",
      formData
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to preview CSV");
    }

    return response as CSVPreviewResult;
  },

  /**
   * Import leads from CSV
   */
  async importCSV(
    file: File,
    options: {
      fieldMapping?: Record<string, string>;
      skipDuplicates?: boolean;
      city?: string;
    } = {}
  ): Promise<CSVImportResult> {
    const { fieldMapping, skipDuplicates = true, city } = options;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("skip_duplicates", skipDuplicates.toString());

    if (fieldMapping) {
      formData.append("field_mapping", JSON.stringify(fieldMapping));
    }

    if (city) {
      formData.append("city", city);
    }

    const response = await apiClient.postFormData(
      "/api/leads/import",
      formData
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to import CSV");
    }

    return response as CSVImportResult;
  },

  /**
   * Get supported import fields
   */
  async getImportFields(): Promise<
    Array<{ field: string; label: string; required: boolean }>
  > {
    const response = await apiClient.get("/api/leads/import/fields");

    if (!response.success) {
      throw new Error(response.error || "Failed to get import fields");
    }

    return response.fields;
  },

  // Lead Notes Methods

  /**
   * Get all notes for a lead
   */
  async getLeadNotes(leadId: string): Promise<LeadNote[]> {
    const response: LeadNotesResponse = await apiClient.get(
      `/api/leads/${leadId}/notes`
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch lead notes");
    }

    return response.notes;
  },

  /**
   * Add a note to a lead
   */
  async addLeadNote(
    leadId: string,
    content: string,
    status?: string
  ): Promise<LeadNote> {
    const response: LeadNoteResponse = await apiClient.post(
      `/api/leads/${leadId}/notes`,
      { content, status }
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to add lead note");
    }

    return response.note;
  },

  /**
   * Delete a lead note
   */
  async deleteLeadNote(leadId: string, noteId: string): Promise<void> {
    const response = await apiClient.delete(
      `/api/leads/${leadId}/notes/${noteId}`
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to delete lead note");
    }
  },

  // Bulk Operations

  /**
   * Bulk update multiple leads
   */
  async bulkUpdate(
    leadIds: string[],
    updates: BulkUpdateData
  ): Promise<BulkUpdateResponse> {
    const response: BulkUpdateResponse = await apiClient.patch("/api/leads/bulk", {
      lead_ids: leadIds,
      updates,
    });

    if (!response.success) {
      throw new Error(response.error || "Failed to bulk update leads");
    }

    return response;
  },

  /**
   * Bulk delete multiple leads
   */
  async bulkDelete(leadIds: string[]): Promise<BulkDeleteResponse> {
    const response: BulkDeleteResponse = await apiClient.delete("/api/leads/bulk", {
      data: { lead_ids: leadIds },
    });

    if (!response.success) {
      throw new Error(response.error || "Failed to bulk delete leads");
    }

    return response;
  },

  // Enrichment

  async enrichLead(
    leadId: string
  ): Promise<{ success: boolean; message: string; lead_id: string }> {
    const response = await apiClient.post(`/api/leads/${leadId}/enrich`);

    if (!response.success) {
      throw new Error(response.error || "Failed to start enrichment");
    }

    return response;
  },

  // Outreach Email Generation

  async generateOutreachEmail(
    leadId: string,
    additionalNotes: string
  ): Promise<{ jobId: string; language: string }> {
    const response = await apiClient.post(
      `/api/leads/${leadId}/generate-outreach`,
      { additional_notes: additionalNotes }
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to queue outreach email");
    }

    return {
      jobId: response.jobId,
      language: response.language,
    };
  },

  // Google Places Discovery

  async searchPlaces(
    category: string,
    city: string,
    country: string = "Italy",
    useCache: boolean = false
  ): Promise<PlacesSearchResult> {
    const response: PlacesSearchResult = await apiClient.post(
      "/api/leads/search/places",
      { category, city, country, use_cache: useCache }
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to search places");
    }

    return response;
  },

  async checkPlacesCache(
    category: string,
    city: string,
    country: string = "Italy"
  ): Promise<{ hasCache: boolean; cachedAt?: string; resultCount?: number }> {
    const response = await apiClient.post(
      "/api/leads/search/places/cache",
      { category, city, country }
    );
    return response;
  },

  async importPlaces(places: PlaceResult[]): Promise<PlacesImportResult> {
    const response: PlacesImportResult = await apiClient.post(
      "/api/leads/import/places",
      { places }
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to import places");
    }

    return response;
  },

  async getPlaceDetails(
    placeId: string
  ): Promise<{ place: PlaceResult & { isExistingLead: boolean } }> {
    const response = await apiClient.post("/api/leads/places/details", {
      place_id: placeId,
    });

    if (!response.success) {
      throw new Error(response.error || "Failed to get place details");
    }

    return { place: response.place };
  },

  // Email Operations

  async sendEmail(
    leadId: string,
    data: SendEmailData
  ): Promise<EmailSend> {
    const response = await apiClient.post(
      `/api/leads/${leadId}/send-email`,
      data
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to send email");
    }

    return response.emailSend;
  },

  async getEmailHistory(leadId: string): Promise<EmailSend[]> {
    const response = await apiClient.get(`/api/leads/${leadId}/emails`);

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch email history");
    }

    return response.emails;
  },

  async getPlaceCategories(): Promise<PlacesCategoriesResult> {
    const response: PlacesCategoriesResult = await apiClient.get(
      "/api/leads/search/places/categories"
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to get categories");
    }

    return response;
  },

  /** DB-owned lead tag catalog (value/viewValue/category/isFormal), TD-041. */
  async getLeadTags(): Promise<LeadTag[]> {
    const response: { success: boolean; tags?: LeadTag[]; error?: string } =
      await apiClient.get("/api/leads/tags");

    if (!response.success) {
      throw new Error(response.error || "Failed to load lead tags");
    }

    return response.tags || [];
  },
};
